import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { DocumentStatus, FileType } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNAButton, DNAText, Iffy } from '@alucio/lux-ui';
import { DocumentVersionORM } from 'src/types/types'
import { useAppSettings } from 'src/state/context/AppSettings'
import { ANALYTIC_CONTEXT } from 'src/state/machines/analytics/analytics.types';
import DNAPopover from '../Popover/DNAPopover';
import { useDNADocumentActions } from './DNADocument.actions';
import { useDNADocumentVersionActions } from './DNADocumentVersion.actions';
import useCurrentPage from '../hooks/useCurrentPage';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';

const styles = StyleSheet.create({
  actionButton: {
    marginLeft: 4,
  },
});

interface DNADocumentVersionActionBarProps {
  documentVersionORM: DocumentVersionORM;
  context?: ANALYTIC_CONTEXT;
}

type ShortcutAction = {
  icon: string;
  key: 'bookmark' | 'download' | 'share' | 'addToFolder';
  onPress: () => void;
  status: 'primary' | 'subtle' | 'tertiary';
  testID: string;
  tooltip: string;
};

const DNADocumentVersionActionBar: React.FC<DNADocumentVersionActionBarProps> = ({
  documentVersionORM,
  context,
}) => {
  const { deviceMode, isOnline } = useAppSettings();
  const documentActions = useDNADocumentActions();
  const documentVersionActions = useDNADocumentVersionActions();
  const route = useCurrentPage({ exact: false });
  const enableFolderManagement = useFeatureFlags('enableFolderManagement');
  const isTablet = deviceMode === 'tablet';
  const isBookmarked = documentVersionORM.relations.documentORM.meta.bookmark.isBookmarked;
  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher');
  const isWebDoc = documentVersionORM.model.type === FileType.WEB;
  const isArchived = [
    DocumentStatus.ARCHIVED,
    DocumentStatus.ARCHIVED_WITH_VERSIONS,
  ].includes(DocumentStatus[documentVersionORM.relations.documentORM.model.status]);
  const canBookmark =
    documentVersionORM.meta.version.isLatestPublished &&
    !isPublisherRoute &&
    documentVersionORM.relations.documentORM.meta.permissions.bookmark;
  const canDownload =
    documentVersionORM.relations.documentORM.meta.permissions.MSLDownload &&
    isOnline &&
    !isWebDoc;
  const canShare =
    documentVersionORM.meta.permissions.MSLShare &&
    isOnline &&
    !isPublisherRoute;
  const canAddToFolder =
    documentVersionORM.relations.documentORM.meta.permissions.addToFolder &&
    !isArchived &&
    enableFolderManagement;

  const actions = useMemo<ShortcutAction[]>(() => {
    const nextActions: ShortcutAction[] = [];

    if (canBookmark) {
      nextActions.push({
        key: 'bookmark',
        icon: isBookmarked ? 'star-fill' : 'star',
        onPress: documentVersionActions.bookmark(documentVersionORM, undefined, undefined, undefined, context),
        status: isBookmarked ? 'primary' : 'subtle',
        testID: 'ai-search-reference-favorite-button',
        tooltip: isBookmarked ? 'Unfavorite' : 'Favorite',
      });
    }

    if (canDownload) {
      nextActions.push({
        key: 'download',
        icon: 'download-simple',
        onPress: documentVersionActions.download(documentVersionORM, undefined, undefined, undefined, context),
        status: 'subtle',
        testID: 'ai-search-reference-download-button',
        tooltip: 'Download',
      });
    }

    if (canShare) {
      nextActions.push({
        key: 'share',
        icon: 'share-fat',
        onPress: documentVersionActions.shareEmail(documentVersionORM, undefined, undefined, undefined, context),
        status: 'subtle',
        testID: 'ai-search-reference-share-button',
        tooltip: 'Share',
      });
    }

    if (canAddToFolder) {
      nextActions.push({
        key: 'addToFolder',
        icon: 'folder-simple-plus',
        onPress: documentActions.addToFolder(documentVersionORM.relations.documentORM, undefined, context),
        status: 'subtle',
        testID: 'ai-search-reference-add-to-folder-button',
        tooltip: 'Add to folder',
      });
    }

    return nextActions;
  }, [
    canAddToFolder,
    canBookmark,
    canDownload,
    canShare,
    documentActions,
    documentVersionActions,
    documentVersionORM,
    isBookmarked,
    context,
  ]);

  return (
    <Iffy is={actions.length > 0}>
      <DNABox appearance="row" alignY="center" wrap="start" fill shrink>
        {actions.map((action, index) => (
          <DNABox
            key={action.key}
            style={index === 0 ? undefined : styles.actionButton}
          >
            <DNAPopover placement="top" offset={4} disablePopover={['tabletPWA']}>
              <DNAPopover.Anchor>
                <DNAButton
                  testID={action.testID}
                  appearance="ghost"
                  status={action.status}
                  size={isTablet ? 'xl' : 'md'}
                  iconLeft={action.icon}
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    action.onPress();
                  }}
                />
              </DNAPopover.Anchor>
              <DNAPopover.Content>
                <DNAText colorMode="dark" numberOfLines={1}>
                  {action.tooltip}
                </DNAText>
              </DNAPopover.Content>
            </DNAPopover>
          </DNABox>
        ))}
      </DNABox>
    </Iffy>
  );
};

export default DNADocumentVersionActionBar;
